import React, { ReactNode } from 'react'

import { UserIcon } from '../../icons'
import { colors } from '../../tokens'
import { Box } from '../box'
import { Icon } from '../icon'
import { Pressable } from '../pressable'
import { Text } from '../text'
import { Tooltip, TooltipContent, TooltipText } from '../tooltip'

const MAX_AVATARS = 3

export type Assignee = {
    id: string | number
    name: string
    color?: string
}

export type AssigneeListProps = {
    assignees: Assignee[]
    /** Tooltip label for the empty-assignees avatar; no tooltip when omitted. */
    unassignedLabel?: string
}

const initials = (name: string) =>
    name
        .split(' ')
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase() ?? '')
        .join('')

const AvatarCircle = ({
    color,
    index,
    children,
}: {
    color: string
    index: number
    children: ReactNode
}) => (
    <Box
        className='-ml-3.5 h-[30px] w-[30px] items-center justify-center rounded-full border border-solid'
        style={{
            backgroundColor: color,
            borderColor: colors['aos-bg'].surface,
            zIndex: MAX_AVATARS - index,
        }}
    >
        {children}
    </Box>
)

export const AssigneeList = ({ assignees, unassignedLabel }: AssigneeListProps) => {
    if (assignees.length === 0) {
        const unassignedAvatar = (
            <AvatarCircle color={colors.graphics.grey} index={0}>
                <Icon as={UserIcon} size={16} className='text-aos-text-default' />
            </AvatarCircle>
        )
        if (!unassignedLabel) {
            return unassignedAvatar
        }
        return (
            <Tooltip
                trigger={triggerProps => (
                    <Pressable
                        {...triggerProps}
                        onHoverIn={triggerProps.onMouseEnter}
                        onHoverOut={triggerProps.onMouseLeave}
                        className='flex-row'
                    >
                        {unassignedAvatar}
                    </Pressable>
                )}
            >
                <TooltipContent>
                    <TooltipText>{unassignedLabel}</TooltipText>
                </TooltipContent>
            </Tooltip>
        )
    }

    return (
        <Tooltip
            trigger={triggerProps => (
                <Pressable
                    {...triggerProps}
                    onHoverIn={triggerProps.onMouseEnter}
                    onHoverOut={triggerProps.onMouseLeave}
                    className='flex-row'
                >
                    {assignees.slice(0, MAX_AVATARS).map((assignee, index) => {
                        const showOverflow =
                            assignees.length > MAX_AVATARS && index === MAX_AVATARS - 1
                        return (
                            <AvatarCircle
                                key={assignee.id}
                                index={index}
                                color={assignee.color ?? colors.graphics.blue}
                            >
                                <Text size='caption' tone='on-graphics' weight='semiBold'>
                                    {showOverflow
                                        ? `+${assignees.length - (MAX_AVATARS - 1)}`
                                        : initials(assignee.name)}
                                </Text>
                            </AvatarCircle>
                        )
                    })}
                </Pressable>
            )}
        >
            <TooltipContent>
                <Box>
                    {assignees.map(assignee => (
                        <TooltipText key={assignee.id}>{assignee.name}</TooltipText>
                    ))}
                </Box>
            </TooltipContent>
        </Tooltip>
    )
}

AssigneeList.displayName = 'AssigneeList'
